import React from 'react'
import { cn } from '../../utils'

interface CardProps {
  children: React.ReactNode
  className?: string
  hover?: boolean
  padding?: 'none' | 'sm' | 'md' | 'lg'
  variant?: 'default' | 'bordered' | 'elevated' | 'ghost'
}

const paddingStyles = {
  none: '',
  sm: 'p-4',
  md: 'p-6',
  lg: 'p-8',
}

const variantStyles = {
  default: cn(
    'bg-white dark:bg-gray-900',
    'border border-gray-200 dark:border-gray-800',
    'shadow-sm'
  ),
  bordered: cn(
    'bg-transparent',
    'border border-gray-200 dark:border-gray-800'
  ),
  elevated: cn(
    'bg-white dark:bg-gray-900',
    'shadow-lg'
  ),
  ghost: cn(
    'bg-gray-50 dark:bg-gray-800/50'
  ),
}

const Card: React.FC<CardProps> & {
  Header: typeof CardHeader
  Body: typeof CardBody
  Footer: typeof CardFooter
} = ({
  children,
  className,
  hover = false,
  padding = 'md',
  variant = 'default',
}) => {
  return (
    <div
      className={cn(
        'rounded-xl overflow-hidden',
        'transition-all duration-200',
        variantStyles[variant],
        paddingStyles[padding],
        hover && 'hover:shadow-md hover:-translate-y-0.5 cursor-pointer',
        className
      )}
    >
      {children}
    </div>
  )
}

interface CardSectionProps {
  children: React.ReactNode
  className?: string
}

const CardHeader: React.FC<CardSectionProps> = ({ children, className }) => (
  <div className={cn('mb-4', className)}>
    {children}
  </div>
)

const CardBody: React.FC<CardSectionProps> = ({ children, className }) => (
  <div className={cn(className)}>
    {children}
  </div>
)

const CardFooter: React.FC<CardSectionProps> = ({ children, className }) => (
  <div className={cn('mt-4 pt-4 border-t border-gray-100 dark:border-gray-800', className)}>
    {children}
  </div>
)

Card.Header = CardHeader
Card.Body = CardBody
Card.Footer = CardFooter

export default Card
